import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  Filter,
  MoreVertical,
  AlertTriangle,
  AlertCircle,
  XCircle,
  X,
  RefreshCcw,
  ArrowUp,
  ArrowDown,
  Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useGetProductsQuery, useDeleteProductMutation, useGetStockThresholdsQuery, useGetDeletedProductsQuery, useRestoreProductMutation, useReorderProductsMutation } from "@/store";
import { TablePagination } from "@/components/admin";
import { toast } from "sonner";
import type { Product } from "@/types/product";

type StockFilter = "all" | "low" | "out" | "in";

const DEFAULT_PAGE_SIZE = 20;

// Helper to get stock alert level
function getStockAlertLevel(stockQuantity: number, inStock: boolean, thresholds: { lowStockThreshold: number; criticalStockThreshold: number }) {
  if (!inStock || stockQuantity <= 0) return "OUT_OF_STOCK";
  if (stockQuantity <= thresholds.criticalStockThreshold) return "CRITICAL";
  if (stockQuantity <= thresholds.lowStockThreshold) return "LOW";
  return "NORMAL";
}

export function AdminProductsScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [activeTab, setActiveTab] = useState("active");
  const [productToRestore, setProductToRestore] = useState<Product | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [orderedProducts, setOrderedProducts] = useState<Product[]>([]);
  const [isReordered, setIsReordered] = useState(false);
  
  // Stock filter from URL params
  const stockFilter = (searchParams.get("stockFilter") as StockFilter) || "all";

  // Get thresholds for stock level calculation
  const { data: thresholdsData } = useGetStockThresholdsQuery();
  const thresholds = thresholdsData?.data || { lowStockThreshold: 10, criticalStockThreshold: 5 };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when stock filter changes
  useEffect(() => {
    setPage(1);
  }, [stockFilter]);

  // Map stock filter to inStock param
  const inStockParam = stockFilter === "in" ? true : stockFilter === "out" ? false : undefined;

  const { data: activeProductsData, isLoading: isActiveLoading } = useGetProductsQuery({
    search: debouncedSearch || undefined,
    page,
    limit,
    inStock: inStockParam,
  }, { skip: activeTab !== "active" });

  const { data: deletedProductsData, isLoading: isDeletedLoading } = useGetDeletedProductsQuery({
    search: debouncedSearch || undefined,
    page,
    limit,
  }, { skip: activeTab !== "deleted" });

  const [deleteProduct, { isLoading: isDeleting }] = useDeleteProductMutation();
  const [restoreProduct, { isLoading: isRestoring }] = useRestoreProductMutation();
  const [reorderProducts, { isLoading: isReordering }] = useReorderProductsMutation();

  const productsData = activeTab === "active" ? activeProductsData : deletedProductsData;
  const isLoading = activeTab === "active" ? isActiveLoading : isDeletedLoading;


  // Filter products based on stock filter (for low stock, we need client-side filtering)
  let products = productsData?.data || [];
  
  // Apply low stock filter client-side since backend doesn't support it directly
  if (stockFilter === "low") {
    products = products.filter((product) => {
      const level = getStockAlertLevel(product.stockQuantity, product.inStock, thresholds);
      return level === "LOW" || level === "CRITICAL" || level === "OUT_OF_STOCK";
    });
  }

  // Initialize orderedProducts when data loads or changes
  useEffect(() => {
    if (products.length > 0 && !isReordered) {
      setOrderedProducts([...products]);
    } else if (products.length === 0) {
      setOrderedProducts([]);
    }
  }, [products]);
  
  const pagination = productsData?.pagination || {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  const handleStockFilterChange = (filter: StockFilter) => {
    if (filter === "all") {
      searchParams.delete("stockFilter");
    } else {
      searchParams.set("stockFilter", filter);
    }
    setSearchParams(searchParams);
  };

  const handleDelete = async () => {
    if (!productToDelete) return;

    try {
      await deleteProduct(productToDelete.id).unwrap();
      toast.success("Product deleted successfully");
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to delete product";
      toast.error(errorMessage);
    }
  };

  const handleRestore = async () => {
    if (!productToRestore) return;

    try {
      await restoreProduct(productToRestore.id).unwrap();
      toast.success("Product restored successfully");
      setRestoreDialogOpen(false);
      setProductToRestore(null);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to restore product";
      toast.error(errorMessage);
    }
  };

  const moveRow = (index: number, direction: -1 | 1) => {
    const newProducts = [...(isReordered ? orderedProducts : products)];
    const temp = newProducts[index];
    newProducts[index] = newProducts[index + direction];
    newProducts[index + direction] = temp;
    setOrderedProducts(newProducts);
    setIsReordered(true);
  };

  const handleSaveOrder = async () => {
    try {
      // Calculate global order based on page and limit
      const startingOrder = (page - 1) * limit;
      const itemsToUpdate = orderedProducts.map((p, index) => ({
        id: p.id,
        order: startingOrder + index,
      }));

      await reorderProducts({ items: itemsToUpdate }).unwrap();
      toast.success("Product order saved successfully");
      setIsReordered(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to save order";
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when changing page size
  };

  const formatPrice = (price: number) => {
    return `₦${price.toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Products</h1>
          <p className="text-muted-foreground">
            Manage your product catalog
          </p>
        </div>
        <div className="flex gap-2">
          {isReordered && activeTab === "active" && (
            <Button
              onClick={handleSaveOrder}
              disabled={isReordering}
              variant="default"
              className="gap-2 bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="h-4 w-4" />
              {isReordering ? "Saving..." : "Save Order"}
            </Button>
          )}
          <Button
            onClick={() => navigate("/admin/products/new")}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search Products</CardTitle>
          <CardDescription>Find products by name or description</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Filter className="h-4 w-4" />
                  Stock Filter
                  {stockFilter !== "all" && (
                    <Badge variant="secondary" className="ml-1">
                      {stockFilter === "low" ? "Low Stock" : stockFilter === "out" ? "Out of Stock" : "In Stock"}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Filter by Stock</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleStockFilterChange("all")}>
                  <Package className="h-4 w-4 mr-2" />
                  All Products
                  {stockFilter === "all" && <span className="ml-auto text-primary">*</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStockFilterChange("low")}>
                  <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                  Low Stock
                  {stockFilter === "low" && <span className="ml-auto text-primary">*</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStockFilterChange("out")}>
                  <XCircle className="h-4 w-4 mr-2 text-red-500" />
                  Out of Stock
                  {stockFilter === "out" && <span className="ml-auto text-primary">*</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleStockFilterChange("in")}>
                  <Package className="h-4 w-4 mr-2 text-green-500" />
                  In Stock
                  {stockFilter === "in" && <span className="ml-auto text-primary">*</span>}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Active filter indicator */}
          {stockFilter !== "all" && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filtered by:</span>
              <Badge variant="secondary" className="gap-1">
                {stockFilter === "low" && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                {stockFilter === "out" && <XCircle className="h-3 w-3 text-red-500" />}
                {stockFilter === "in" && <Package className="h-3 w-3 text-green-500" />}
                {stockFilter === "low" ? "Low Stock Items" : stockFilter === "out" ? "Out of Stock" : "In Stock"}
                <button
                  onClick={() => handleStockFilterChange("all")}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setPage(1); }}>
        <div className="flex justify-between items-center mb-4">
          <TabsList>
            <TabsTrigger value="active">Active Products</TabsTrigger>
            <TabsTrigger value="deleted">Deleted Products</TabsTrigger>
          </TabsList>
        </div>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Products</CardTitle>
          <CardDescription>
            {pagination.total} {pagination.total === 1 ? "product" : "products"} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading products...
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No products found</p>
              <Button onClick={() => navigate("/admin/products/new")}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Product
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {activeTab === "active" && <TableHead className="w-[80px]">Order</TableHead>}
                      <TableHead className="w-[100px]">Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Stock</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(isReordered ? orderedProducts : products).map((product, index) => (
                      <TableRow key={product.id}>
                        {activeTab === "active" && (
                          <TableCell>
                            <div className="flex flex-col items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 rounded-full"
                                disabled={index === 0}
                                onClick={() => moveRow(index, -1)}
                              >
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6 rounded-full"
                                disabled={index === (isReordered ? orderedProducts.length : products.length) - 1}
                                onClick={() => moveRow(index, 1)}
                              >
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="h-12 w-12 rounded-md overflow-hidden bg-muted">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div>
                            <p className="font-semibold">{product.name}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {product.description}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {product.category?.name || "Uncategorized"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {formatPrice(product.price)}
                            </span>
                            {product.onSale && product.originalPrice && (
                              <span className="text-xs text-muted-foreground line-through">
                                {formatPrice(product.originalPrice)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const level = getStockAlertLevel(product.stockQuantity, product.inStock, thresholds);
                            return (
                              <div className="flex items-center gap-2">
                                {level === "OUT_OF_STOCK" && (
                                  <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                )}
                                {level === "CRITICAL" && (
                                  <AlertCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                                )}
                                {level === "LOW" && (
                                  <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                                )}
                                <div className="flex flex-col">
                                  <span
                                    className={
                                      level === "OUT_OF_STOCK"
                                        ? "text-red-600 dark:text-red-400 font-medium"
                                        : level === "CRITICAL"
                                        ? "text-orange-600 dark:text-orange-400 font-medium"
                                        : level === "LOW"
                                        ? "text-yellow-600 dark:text-yellow-400 font-medium"
                                        : "text-green-600 dark:text-green-400"
                                    }
                                  >
                                    {product.stockQuantity}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {level === "OUT_OF_STOCK"
                                      ? "Out of Stock"
                                      : level === "CRITICAL"
                                      ? "Critical"
                                      : level === "LOW"
                                      ? "Low Stock"
                                      : "In Stock"}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {product.featured && (
                              <Badge variant="default" className="w-fit">
                                Featured
                              </Badge>
                            )}
                            {product.onSale && (
                              <Badge variant="secondary" className="w-fit">
                                On Sale
                              </Badge>
                            )}
                            {product.badge && (
                              <Badge variant="outline" className="w-fit">
                                {product.badge}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {activeTab === "active" ? (
                                <>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      navigate(`/admin/products/${product.id}/edit`)
                                    }
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setProductToDelete(product);
                                      setDeleteDialogOpen(true);
                                    }}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setProductToRestore(product);
                                    setRestoreDialogOpen(true);
                                  }}
                                >
                                  <RefreshCcw className="h-4 w-4 mr-2" />
                                  Restore
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <TablePagination
                pagination={pagination}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </>
          )}
        </CardContent>
      </Card>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{productToDelete?.name}". This action
              cannot be undone. If this product has been ordered, you cannot
              delete it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setProductToDelete(null);
                setDeleteDialogOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Product</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore "{productToRestore?.name}" and make it visible again. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setProductToRestore(null);
                setRestoreDialogOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={isRestoring}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isRestoring ? "Restoring..." : "Restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
